// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { LinkedSystem } from '../misc/LinkedSystem';
import TimeSalesPanelTemplate from '@front/templates/TimeSalesPanelTemplate.html';
import { TimeSalesItem, TSItemType } from '../cache/TimeSalesItem';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { ThemeManager } from '../misc/ThemeManager';
import { AgressorFlagType } from '@shared/utils/Enums/Constants';
import { RouteUtils } from '@shared/commons/cache/RouteUtils';
import { DynProperty, PairColor } from '@shared/commons/DynProperty';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
export class TimeSalesPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this.isSeparatedSourceMode = null;
        this.rowIdCounter = 0;
        this.cMIDLO = null;
        this.cBIDLO = null;
        this.cBID = null;
        this.cASKHI = null;
        this.cASK = null;
        this.cQUOTE = null;
        this.cASKHIbackground = null;
        this.cASKbackground = null;
        this.cBIDbackground = null;
        this.cBIDLObackground = null;
        this.cMIDLObackground = null;
        this.cQUOTEbackground = null;
        this.Name = 'TimeSalesPanel';
        this.NeedCalculateRowCount = false;
        this.headerLocaleKey = 'panel.timeSales';
    }
    getType() {
        return PanelNames.TimeSalesPanel;
    }
    oninit() {
        super.oninit();
        this.observe('instrument', this.changeInstrument);
    }
    oncomplete() {
        super.oncomplete();
        this.applyTheme();
        this.changeInstrument(this.get('instrument'), null);
    }
    updatePanelHeader() {
        const ins = this.get('instrument');
        void this.set({
            header: Resources.getResource(this.headerLocaleKey) + (!isNullOrUndefined(ins) ? ' ' + ins.DisplayName() : '')
        });
    }
    changeInstrument(instrument, lastInstrument) {
        if (isNullOrUndefined(instrument) || instrument === lastInstrument || !this.completed) {
            return;
        }
        this.isSeparatedSourceMode = RouteUtils.isSeparatedSourceMode(instrument);
        this.unsubscribeInstrument(lastInstrument);
        this.clearTable();
        this.subscribeInstrument();
        this.symbolLink_Out(false, instrument);
        this.updatePanelHeader();
    }
    // #region ICaller
    callBack(properties) {
        super.callBack(properties);
        void this.set('instrument', this.getCallBackInstrument(properties, 'symbol'));
    }
    Properties() {
        const properties = super.Properties();
        const ins = this.get('instrument');
        if (!isNullOrUndefined(ins)) {
            properties.push(new DynProperty('symbol', ins.GetInteriorID(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        return properties;
    }
    // #endregion
    subscribeInstrument() {
        const value = this.get('instrument');
        if (!isNullOrUndefined(value === null || value === void 0 ? void 0 : value.DataCache)) {
            value.DataCache.FQuoteCache.addListener(value, this, HistoryType.QUOTE_LEVEL1);
            value.DataCache.FQuoteCache.addListener(value, this, HistoryType.QUOTE_TRADES);
        }
    }
    unsubscribeInstrument(lastInstrument) {
        if (isNullOrUndefined(lastInstrument === null || lastInstrument === void 0 ? void 0 : lastInstrument.DataCache)) {
            return;
        }
        lastInstrument.DataCache.FQuoteCache.removeListener(lastInstrument, this, HistoryType.QUOTE_LEVEL1);
        lastInstrument.DataCache.FQuoteCache.removeListener(lastInstrument, this, HistoryType.QUOTE_TRADES);
    }
    newQuote(tradeMessage) {
        if (tradeMessage.Type !== HistoryType.QUOTE_TRADES) {
            return;
        }
        const ins = this.get('instrument');
        if (isNullOrUndefined(ins)) {
            return;
        }
        if (isNullOrUndefined(this.quickTableRactive)) {
            return;
        }
        // Fix for continuous contracts.
        if (ins.GetInteriorID() !== tradeMessage.TargetInstrumentName) {
            return;
        }
        let size = tradeMessage.Size;
        const inLots = WDSettingsUtils.displayAmountInLots();
        if (inLots) {
            size = Quantity.convertQuantityValue(new Quantity(size, false), ins, inLots, this.getAccount() /* , productType /* TODO ? */);
        }
        const lastQuote = ins.GetLastQuote(QuoteValid.Last);
        if (isNullOrUndefined(lastQuote)) {
            return;
        }
        const res = TimeSalesPanel.getTimeSalesItemData(tradeMessage, lastQuote.Bid, lastQuote.Ask, true);
        const item = new TimeSalesItem(SessionSettings);
        item.ItemId = this.rowIdCounter++;
        item.instrument = ins;
        item.isSeparatedSourceMode = this.isSeparatedSourceMode;
        item.aggressorFlag = res.correctAggressorFlag;
        item.size = size;
        item.colorValue = res.colorValue;
        item.itemType = res.tsItemType;
        item.time = new Date(tradeMessage.cTime);
        item.price = tradeMessage.Price;
        item.exchange = tradeMessage.Exchange;
        item.lastVenue = tradeMessage.LastVenue;
        item.buyerSource = tradeMessage.BuyerSource;
        item.sellerSource = tradeMessage.SellerSource;
        const qt = this.getQuickTable();
        const row = qt.AddItem(item);
        if (!isNullOrUndefined(row)) {
            // TODO. Ugly.
            const colors = this.getRowColors(item.itemType);
            row.ForeColor = colors.Color1;
            row.BackColor = colors.Color2;
        }
        // TODO. Removing the oldest item.
        if (qt.rowsArray.length > TimeSalesPanel.MAX_ROW_COUNT) {
            qt.RemoveItem(this.rowIdCounter - 1 - TimeSalesPanel.MAX_ROW_COUNT);
        }
        // TODO. Ugly.
        qt.needRedrawBackground = true;
        qt.needRedraw = true;
    }
    dispose() {
        super.dispose();
        this.unsubscribeInstrument(this.get('instrument'));
    }
    jbInit() {
        super.jbInit();
        this.getQuickTable().AddToEnd = false;
        this.getQuickTable().lockManualSorting = true;
        this.updateHiddenColumns();
    }
    updateHiddenColumns() {
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt)) {
            return;
        }
        const isSeparatedSourceMode = this.isSeparatedSourceMode;
        qt.setHiddenColumnsData({
            /* Exchange */ 4: isSeparatedSourceMode || Resources.isHidden(qt.columns[4].headerKey),
            /* Buyer */ 12: !isSeparatedSourceMode || Resources.isHidden(qt.columns[12].headerKey),
            /* Seller */ 13: !isSeparatedSourceMode || Resources.isHidden(qt.columns[13].headerKey)
        });
    }
    symbolLink_Out(newSubscriber, instrument) {
        if (isNullOrUndefined(instrument)) {
            const ins = this.get('instrument');
            if (isNullOrUndefined(ins))
                return;
            instrument = ins;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber);
        }
    }
    getInstrument() {
        return this.get('instrument');
    }
    symbolLink_In(symbolName) {
        const newInstr = DataCache.getInstrumentByName(symbolName);
        if (!isNullOrUndefined(newInstr)) {
            void this.set('instrument', newInstr);
        }
    }
    repopulate() {
        super.repopulate();
        void this.set('instrument', this.get('instrument'));
    }
    getRowColors(tsItemType) {
        switch (tsItemType) {
            case TSItemType.AboveAsk:
                return new PairColor(this.cASKHI, this.cASKHIbackground);
            case TSItemType.AtAsk:
                return new PairColor(this.cASK, this.cASKbackground);
            case TSItemType.AtBid:
                return new PairColor(this.cBID, this.cBIDbackground);
            case TSItemType.BelowBid:
                return new PairColor(this.cBIDLO, this.cBIDLObackground);
            case TSItemType.BetweenSpread:
                return new PairColor(this.cMIDLO, this.cMIDLObackground);
            case TSItemType.Quotes:
                return new PairColor(this.cQUOTE, this.cQUOTEbackground);
            default:
                return null;
        }
    }
    themeChange() {
        super.themeChange();
        const theme = ThemeManager.colors;
        this.cMIDLO = theme.TS_cMIDLO;
        this.cBIDLO = theme.TS_cBIDLO;
        this.cBID = theme.TS_cBID;
        this.cASKHI = theme.TS_cASKHI;
        this.cASK = theme.TS_cASK;
        this.cQUOTE = theme.TS_cQUOTE;
        this.cASKHIbackground = theme.TS_cASKHIbackground;
        this.cASKbackground = theme.TS_cASKbackground;
        this.cBIDbackground = theme.TS_cBIDbackground;
        this.cBIDLObackground = theme.TS_cBIDLObackground;
        this.cMIDLObackground = theme.TS_cMIDLObackground;
        this.cQUOTEbackground = theme.TS_cQUOTEbackground;
        const qtRactive = this.quickTableRactive;
        if (isNullOrUndefined(qtRactive))
            return;
        const qt = qtRactive.quickTable;
        if (isNullOrUndefined(qt))
            return;
        qt.selectedForeColor = ThemeManager.colors.timeAndSalesRowSelectedForeground;
        qt.selectedBackColor = ThemeManager.colors.timeAndSalesRowSelectedBackground;
        const rowDict = qt.rows;
        if (isNullOrUndefined(rowDict))
            return;
        for (const key in rowDict) {
            const item = rowDict[key].item;
            if (isNullOrUndefined(item))
                continue;
            const colors = this.getRowColors(item.itemType);
            const row = qt.rows[item.ItemId];
            if (!isNullOrUndefined(colors)) {
                row.ForeColor = colors.Color1;
                row.BackColor = colors.Color2;
            }
        }
        // TODO. Ugly.
        qt.needRedrawBackground = true;
        qt.needRedraw = true;
    }
    // TODO. Rename.
    // Returns colorValue, agressorFlag, TSItemType.
    static getTimeSalesItemData(msg, bid, ask, realTime) {
        let correctAggressorFlag = msg.LType;
        let tsItemType = null;
        let colorValue = null;
        const aggressorFlagTypes = AgressorFlagType;
        // CrossTrade & Auction
        if (correctAggressorFlag === aggressorFlagTypes.CrossTrade ||
            correctAggressorFlag === aggressorFlagTypes.Auction) {
            if (msg.Price < bid) {
                colorValue = 1;
                tsItemType = TSItemType.BelowBid;
            }
            else if (msg.Price === bid) {
                colorValue = 2;
                tsItemType = TSItemType.AtBid;
            }
            else if (msg.Price > ask) {
                colorValue = 3;
                tsItemType = TSItemType.AboveAsk;
            }
            else if (msg.Price === ask) {
                colorValue = 4;
                tsItemType = TSItemType.AtAsk;
            }
            else {
                colorValue = 0;
                tsItemType = TSItemType.BetweenSpread;
            }
            // Bid >= Ask - красим по агрессору, или серым
        }
        else if (bid === 0 || bid >= ask || !realTime) {
            // если есть агрессор флаг красим по нему
            if (correctAggressorFlag === aggressorFlagTypes.Ask) {
                colorValue = 4;
                tsItemType = TSItemType.AtAsk;
            }
            else if (correctAggressorFlag === aggressorFlagTypes.Bid) {
                colorValue = 2;
                tsItemType = TSItemType.AtBid;
            }
            else {
                colorValue = 0;
                tsItemType = TSItemType.BetweenSpread;
            }
            // below bid
        }
        else if (msg.Price < bid) {
            // совпадает с агрессор флаг - класс, нет возврящаем цвет по аггрессор флагу
            if (correctAggressorFlag === aggressorFlagTypes.Bid ||
                correctAggressorFlag === aggressorFlagTypes.None ||
                correctAggressorFlag === aggressorFlagTypes.NotSet) {
                correctAggressorFlag = aggressorFlagTypes.Bid;
                colorValue = 1;
                tsItemType = TSItemType.BelowBid;
            }
            else {
                correctAggressorFlag = aggressorFlagTypes.Ask;
                colorValue = 4;
                tsItemType = TSItemType.AtAsk;
            }
            // at bid
        }
        else if (msg.Price === bid) {
            // совпадает с агрессор флаг - класс, нет возврящаем цвет по аггрессор флагу
            if (correctAggressorFlag === aggressorFlagTypes.Bid ||
                correctAggressorFlag === aggressorFlagTypes.None ||
                correctAggressorFlag === aggressorFlagTypes.NotSet) {
                correctAggressorFlag = aggressorFlagTypes.Bid;
                colorValue = 2;
                tsItemType = TSItemType.AtBid;
            }
            else {
                correctAggressorFlag = aggressorFlagTypes.Ask;
                colorValue = 4;
                tsItemType = TSItemType.AtAsk;
            }
            // above ask
        }
        else if (msg.Price > ask) {
            // совпадает с агрессор флаг - класс, нет возврящаем цвет по аггрессор флагу
            if (correctAggressorFlag === aggressorFlagTypes.Ask ||
                correctAggressorFlag === aggressorFlagTypes.None ||
                correctAggressorFlag === aggressorFlagTypes.NotSet) {
                correctAggressorFlag = aggressorFlagTypes.Ask;
                colorValue = 3;
                tsItemType = TSItemType.AboveAsk;
            }
            else {
                correctAggressorFlag = aggressorFlagTypes.Bid;
                colorValue = 2;
                tsItemType = TSItemType.AtBid;
            }
        }
        else if (msg.Price === ask) {
            // at ask
            // совпадает с агрессор флаг - класс, нет возврящаем цвет по аггрессор флагу
            if (correctAggressorFlag === aggressorFlagTypes.Ask ||
                correctAggressorFlag === aggressorFlagTypes.None ||
                correctAggressorFlag === aggressorFlagTypes.NotSet) {
                correctAggressorFlag = aggressorFlagTypes.Ask;
                colorValue = 4;
                tsItemType = TSItemType.AtAsk;
            }
            else {
                correctAggressorFlag = aggressorFlagTypes.Bid;
                colorValue = 2;
                tsItemType = TSItemType.AtBid;
            }
            // спред
        }
        else {
            // если есть агрессор флаг красим по нему
            if (correctAggressorFlag === aggressorFlagTypes.Ask) {
                colorValue = 4;
                tsItemType = TSItemType.AtAsk;
            }
            else if (correctAggressorFlag === aggressorFlagTypes.Bid) {
                colorValue = 2;
                tsItemType = TSItemType.AtBid;
                // если нет флага то серым
            }
            else {
                // Price is in between the bid and ask. Calc if closer to bid or to ask.
                const dSpread = ask - bid;
                const dMid = bid + dSpread / 2;
                if (msg.Price < dMid) {
                    colorValue = 5;
                    tsItemType = TSItemType.BetweenSpread;
                }
                else {
                    colorValue = 6;
                    tsItemType = TSItemType.BetweenSpread;
                }
            }
        }
        return {
            colorValue,
            tsItemType,
            correctAggressorFlag
        };
    }
}
TimeSalesPanel.MAX_ROW_COUNT = 500;
ApplicationPanelWithTable.extendWith(TimeSalesPanel, {
    data: function () {
        return {
            instrument: null,
            account: null,
            isSymbolLinkShow: true,
            isAccountLinkShow: true,
            canFilterByAccount: false
        };
    },
    partials: {
        bodyPartial: TimeSalesPanelTemplate
    }
});
